import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Stack,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  PlayArrow as StartIcon,
  ArrowForward as NextIcon,
} from '@mui/icons-material';
import { useAppSelector } from '../hooks/reduxHooks';
import { WorkflowStage } from '../store/slices/workflowSlice';
import DssPipelineStrip from '../components/workflow/DssPipelineStrip';

const STEPS: {
  stage: WorkflowStage;
  title: string;
  purpose: string;
  route: string;
}[] = [
  {
    stage: WorkflowStage.WATER_PROFILE,
    title: 'Water',
    purpose: 'Source ion profile (ppm)',
    route: '/water-quality',
  },
  {
    stage: WorkflowStage.CROP_FORMULATION,
    title: 'Crop',
    purpose: 'Target nutrient setpoints',
    route: '/formulation',
  },
  {
    stage: WorkflowStage.SALT_MANAGEMENT,
    title: 'Salts',
    purpose: 'Fertilizer salt set',
    route: '/salt-library',
  },
  {
    stage: WorkflowStage.STOCK_SOLUTIONS,
    title: 'Recipe',
    purpose: 'Solve amounts, A/B, verify',
    route: '/stock-solutions',
  },
  {
    stage: WorkflowStage.UNIFIED_CALCULATOR,
    title: 'Results',
    purpose: 'Report and export',
    route: '/results',
  },
];

function stageDone(
  stage: WorkflowStage,
  stageStatus: Record<string, { completed?: boolean }>
): boolean {
  if (stage === WorkflowStage.UNIFIED_CALCULATOR) {
    return (
      Boolean(stageStatus[stage]?.completed) ||
      Boolean(stageStatus[WorkflowStage.STOCK_SOLUTIONS]?.completed)
    );
  }
  return Boolean(stageStatus[stage]?.completed);
}

const DashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { stageStatus, waterProfile, formulation, saltSelection, stockSolution } =
    useAppSelector((state) => state.workflow);

  const completedCount = STEPS.filter((s) => stageDone(s.stage, stageStatus)).length;

  const next =
    STEPS.find((s) => !stageDone(s.stage, stageStatus)) ?? null;

  const progressPct = Math.round((completedCount / STEPS.length) * 100);

  const contextBits = [
    waterProfile?.name ? `Water: ${waterProfile.name}` : null,
    formulation?.name ? `Crop: ${formulation.name}` : null,
    saltSelection?.selectedSalts?.length
      ? `Salts: ${saltSelection.selectedSalts.length}`
      : null,
    stockSolution?.volume != null
      ? `V: ${stockSolution.volume}${(stockSolution as any).volumeUnit || 'L'}`
      : null,
  ].filter(Boolean) as string[];

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" fontWeight={600}>
          Workspace
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Nutrient formulation pipeline — inputs, solve, verify, export.
        </Typography>
      </Box>

      <DssPipelineStrip />

      {contextBits.length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
          {contextBits.map((bit) => (
            <Chip key={bit} label={bit} size="small" variant="outlined" />
          ))}
        </Stack>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ sm: 'center' }}
          justifyContent="space-between"
          spacing={1.5}
          sx={{ mb: 1.5 }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {completedCount}/{STEPS.length} complete · {progressPct}%
          </Typography>
          {next ? (
            <Button
              variant="contained"
              size="small"
              startIcon={<StartIcon />}
              endIcon={<NextIcon />}
              onClick={() => navigate(next.route)}
            >
              Continue: {next.title}
            </Button>
          ) : (
            <Button variant="outlined" size="small" onClick={() => navigate('/results')}>
              Open results
            </Button>
          )}
        </Stack>
        <LinearProgress
          variant="determinate"
          value={progressPct}
          sx={{ height: 6, borderRadius: 1 }}
        />
      </Paper>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" aria-label="Recipe pipeline stages">
          <TableHead>
            <TableRow>
              <TableCell width={48}>#</TableCell>
              <TableCell>Stage</TableCell>
              <TableCell>Purpose</TableCell>
              <TableCell width={100}>Status</TableCell>
              <TableCell width={120} align="right">
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {STEPS.map((step, index) => {
              const done = stageDone(step.stage, stageStatus);
              const isNext = next?.stage === step.stage;

              return (
                <TableRow
                  key={step.stage}
                  hover
                  selected={isNext}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(step.route)}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {step.title}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {step.purpose}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {done ? (
                      <Chip
                        icon={<CheckIcon />}
                        label="Done"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : isNext ? (
                      <Chip label="Next" size="small" color="primary" />
                    ) : (
                      <Chip label="—" size="small" variant="outlined" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant={isNext ? 'contained' : 'text'}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(step.route);
                      }}
                    >
                      {done ? 'Open' : isNext ? 'Start' : 'Open'}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default DashboardPage;
